import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle, Loader2, XCircle } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import API_BASE_URL from '@/utils/apiUrl';

const API_URL = API_BASE_URL;

export default function PayPalSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser, user } = useAuth();
  const [status, setStatus] = useState('processing');
  const [message, setMessage] = useState('Apdorojamas mokėjimas...');
  const [planName, setPlanName] = useState('');

  useEffect(() => {
    const executePayment = async () => {
      // Orders API v2 uses 'token', legacy API uses 'paymentId'
      const token = searchParams.get('token');
      const paymentId = searchParams.get('paymentId');
      const payerId = searchParams.get('PayerID');
      const plan = searchParams.get('plan');
      const email = searchParams.get('email');

      // Check for Orders API v2 (token) or legacy API (paymentId)
      const orderId = token || paymentId;
      
      if (!orderId || !plan || !email) {
        setStatus('error');
        setMessage('Trūksta mokėjimo duomenų');
        return;
      }

      try {
        // Use appropriate endpoint based on API version
        let url;
        if (token) {
          // Orders API v2 - use capture endpoint
          url = `${API_URL}/api/paypal/capture-paypal-order?token=${token}&plan=${plan}&email=${encodeURIComponent(email)}`;
        } else {
          // Legacy API - use execute endpoint
          url = `${API_URL}/api/paypal/execute-paypal-payment?paymentId=${paymentId}&PayerID=${payerId}&plan=${plan}&email=${encodeURIComponent(email)}`;
        }
        
        const response = await fetch(url);
        const data = await response.json();

        if (data.success) {
          setStatus('success');
          setMessage('Mokėjimas sėkmingas!');
          setPlanName(data.planName || 'VIP');
          
          // Update localStorage
          localStorage.setItem('isVIP', 'true');
          localStorage.setItem('vipPlan', plan);
          localStorage.setItem('vipEmail', email);
          
          // CRITICAL: Refresh user session to get updated VIP status
          await refreshUser();
          
          // CRITICAL: Reset numerologyData to the authenticated user's data to avoid session mix-up
          // This ensures users see their own profile after payment, not someone else's from "Mano ratas"
          if (user) {
            const userData = {
              firstName: user.firstName,
              lastName: user.lastName,
              day: user.day,
              month: user.month,
              year: user.year,
              lifePath: user.lifePath,
              gender: user.gender || null
            };
            localStorage.setItem('numerologyData', JSON.stringify(userData));
          }
        } else {
          setStatus('error');
          setMessage(data.detail || 'Mokėjimo klaida');
        }
      } catch (error) {
        console.error('PayPal execution error:', error);
        setStatus('error');
        setMessage('Serverio klaida. Prašome susisiekti su palaikymu.');
      }
    };

    executePayment();
  }, [searchParams, refreshUser, user]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0f0f23] via-[#1a1a3e] to-[#2d1b4e] flex items-center justify-center p-4">
      <Card className="max-w-md w-full p-8 bg-[#1a1a3e]/80 border-[#d4af37]/30 text-center">
        {status === 'processing' && (
          <>
            <Loader2 className="w-16 h-16 text-[#d4af37] animate-spin mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-[#d4af37] mb-2">Apdorojama...</h1>
            <p className="text-gray-300">{message}</p>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-[#d4af37] mb-2">Sveikiname! 🎉</h1>
            <p className="text-gray-300 mb-2">{message}</p>
            <p className="text-[#d4af37] font-medium mb-6">
              Jūsų planas: {planName}
            </p>
            <Button 
              onClick={() => navigate('/rezultatai')}
              className="w-full bg-gradient-to-r from-[#d4af37] to-[#f4cf67] text-[#1a1a3e] font-bold hover:opacity-90"
            >
              Tęsti į rezultatus
            </Button>
          </>
        )}

        {status === 'error' && (
          <>
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-red-400 mb-2">Klaida</h1>
            <p className="text-gray-300 mb-6">{message}</p>
            <Button 
              onClick={() => navigate('/')}
              variant="outline"
              className="w-full border-[#d4af37]/50 text-[#d4af37]"
            >
              Grįžti į pradžią
            </Button>
          </>
        )}
      </Card>
    </div>
  );
}
